import {
  ArrowRightIcon,
  ChartBarIcon,
  ChatAlt2Icon,
  CheckIcon,
  CloudIcon,
  DocumentTextIcon,
  FolderIcon,
  LinkIcon,
  MailIcon,
  ShieldCheckIcon,
  SwitchHorizontalIcon,
} from "@heroicons/react/solid";
import React from "react";
import { trackEvent } from "../analytics";
import { moreSolutions, solutionPacks } from "../data";

const icons = {
  "identity-security": ShieldCheckIcon,
  "api-integration": LinkIcon,
  "messaging-resilience": SwitchHorizontalIcon,
  "email-notifications": MailIcon,
  "documents-files": FolderIcon,
  "reporting-dashboards": ChartBarIcon,
  "aws-readiness": CloudIcon,
  "ai-document-processing": DocumentTextIcon,
  "ai-assistants": ChatAlt2Icon,
};

const groups = [
  {
    id: "foundation",
    title: "Foundation packs",
    blurb:
      "The building blocks almost every application needs, from login to reporting.",
  },
  {
    id: "cloud-ai",
    title: "Cloud and AI",
    blurb: "Move to the cloud with a plan, and use AI where it earns its place.",
  },
];

function AskLink({ location, children }) {
  return (
    <a
      href="#contact"
      onClick={() => trackEvent("cta_click", { location })}
      className="inline-flex items-center text-purple-600 text-sm font-medium hover:text-purple-800"
    >
      {children}
      <ArrowRightIcon className="w-4 h-4 ml-1" />
    </a>
  );
}

function GroupHeading({ title, blurb }) {
  return (
    <div className="mb-4">
      <h2 className="text-gray-900 text-2xl title-font font-semibold">
        {title}
      </h2>
      <p className="text-gray-600 text-sm">{blurb}</p>
    </div>
  );
}

function PackCard({ pack }) {
  const Icon = icons[pack.slug];
  const ready = pack.status === "ready";
  return (
    <div className="bg-white rounded-xl p-8 flex flex-col h-full border border-purple-100 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl bg-purple-600 flex items-center justify-center">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <span
          className={
            ready
              ? "text-xs font-semibold rounded-full px-3 py-1 bg-purple-600 text-white"
              : "text-xs font-medium rounded-full px-3 py-1 border border-gray-300 text-gray-600"
          }
        >
          {ready ? "Ready to start" : "Scoped to you"}
        </span>
      </div>
      <h3 className="text-gray-900 text-xl title-font font-medium mb-2">
        {pack.title}
      </h3>
      <p className="text-purple-600 text-sm font-medium mb-3">{pack.problem}</p>
      <p className="text-gray-600 text-sm leading-relaxed mb-6">
        {pack.outcome}
      </p>
      <ul className="space-y-2 mb-6">
        {pack.includes.map((item) => (
          <li key={item} className="flex items-start text-sm text-gray-700">
            <CheckIcon className="text-purple-600 w-4 h-4 flex-shrink-0 mr-2 mt-0.5" />
            {item}
          </li>
        ))}
      </ul>
      <p className="text-xs uppercase tracking-wide text-gray-500 font-medium mt-auto mb-3">
        {ready
          ? pack.engagement
          : "Scoped and quoted after a short call"}
      </p>
      <AskLink location={`solution_pack_${pack.slug}`}>Ask about this</AskLink>
    </div>
  );
}

export default function SolutionPacks() {
  return (
    <section id="solution-packs" className="bg-purple-50 text-gray-900">
      <div className="container px-5 py-16 mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold title-font text-gray-900 mb-4">
            Solution Packs
          </h1>
          <p className="text-base leading-relaxed xl:w-2/4 lg:w-3/4 mx-auto text-gray-600">
            Clear, scoped ways to work with us, alongside our consultancy,
            product development and ongoing support. Packs marked{" "}
            <span className="font-medium text-gray-800">Ready to start</span>{" "}
            are built and tested. The rest are scoped around your situation.
          </p>
        </div>

        {groups.map((group) => (
          <div key={group.id} className="mb-12">
            <GroupHeading title={group.title} blurb={group.blurb} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {solutionPacks
                .filter((pack) => pack.group === group.id)
                .map((pack) => (
                  <PackCard key={pack.slug} pack={pack} />
                ))}
            </div>
          </div>
        ))}

        <GroupHeading
          title="Platform and data"
          blurb="Scoped to your situation and quoted after a short call."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {moreSolutions.map((item) => (
            <div
              key={item.title}
              className="bg-white rounded-xl p-6 flex flex-col border border-purple-100"
            >
              <h3 className="text-gray-900 text-base title-font font-medium mb-1">
                {item.title}
              </h3>
              <p className="text-purple-600 text-sm font-medium mb-2">
                {item.summary}
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                {item.covers}
              </p>
              <div className="mt-auto">
                <AskLink
                  location={`solution_more_${item.title
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "_")}`}
                >
                  Ask about this
                </AskLink>
              </div>
            </div>
          ))}
          <div className="bg-purple-600 rounded-xl p-6 flex flex-col">
            <h3 className="text-white text-base title-font font-medium mb-1">
              Something different?
            </h3>
            <p className="text-purple-100 text-sm leading-relaxed mb-4">
              Most of our work is custom. If your problem is not listed, tell
              us what you are trying to fix.
            </p>
            <a
              href="#contact"
              onClick={() =>
                trackEvent("cta_click", { location: "solution_packs_custom" })
              }
              className="mt-auto inline-flex items-center text-white text-sm font-medium hover:text-purple-100"
            >
              Talk to us
              <ArrowRightIcon className="w-4 h-4 ml-1" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
